import {
  Box,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { ColumnAlignment, Row, TableInstance, TableRowProps } from 'react-table';
import { ContractReviewGridItem } from 'shared/types';
import { useContractReviewListState } from 'approval-workflow/state/contractReviewListState';
import { useEffect, useRef } from 'react';
import { ContractApprovalWorkflowTableState } from 'shared/contexts/contract-approval-workflow-filter-context';
import { useDebounce } from 'shared/hooks';
import { ContractReviewTablePagination } from './contract-review-table-pagination';

const containerStyles: SxProps<Theme> = {
  maxHeight: '40vh',
  minHeight: '100px',
};

const sortStyles: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
};

const getHeaderFlexAlignment = (align: ColumnAlignment) => {
  switch (align) {
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'flex-start';
  }
};

interface ContractReviewTableProps {
  instance: TableInstance<ContractReviewGridItem>;
  setContractApprovalWorkflowTableState: React.Dispatch<React.SetStateAction<ContractApprovalWorkflowTableState>>;
  totalCount: number;
}

export function ContractReviewTable({
  instance,
  totalCount,
  setContractApprovalWorkflowTableState,
}: ContractReviewTableProps) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page, state } = instance;

  const firstRender = useRef(true);
  const debouncedState = useDebounce(state, 500);

  useEffect(() => {
    const { pageIndex, pageSize, sortBy, filters, globalFilter, hiddenColumns } = debouncedState;
    if (!firstRender.current) {
      setContractApprovalWorkflowTableState(prevState => {
        return {
          ...prevState,
          tableState: {
            ...prevState.tableState,
            pageIndex,
            pageSize,
            sortBy,
            filters,
            globalFilter,
            hiddenColumns,
          },
        };
      });
    }
    firstRender.current = false;
  }, [debouncedState, setContractApprovalWorkflowTableState]);

  const headers = headerGroups.map(headerGroup => (
    <TableRow {...headerGroup.getHeaderGroupProps()}>
      {headerGroup.headers.map(column => (
        <TableCell
          align={column.align}
          {...column.getHeaderProps({
            style: {
              width: column.width,
              minWidth: column.minWidth,
            },
          })}
        >
          <Box
            component='span'
            sx={sortStyles}
            {...column.getSortByToggleProps({ style: { justifyContent: getHeaderFlexAlignment(column.align) } })}
          >
            {column.render('Header')}
            {column.isSorted ? column.isSortedDesc ? <ArrowDropDown /> : <ArrowDropUp /> : null}
          </Box>
        </TableCell>
      ))}
    </TableRow>
  ));

  const rows = page.length
    ? page.map((row, i) => {
        prepareRow(row);
        const { key, ...rest } = row.getRowProps();
        return <ContractReviewTableRow key={key} row={row} {...rest} />;
      })
    : [
        <TableRow key='no-data'>
          <TableCell colSpan={8}>
            <Typography>No contracts have been submitted for review.</Typography>
          </TableCell>
        </TableRow>,
      ];
  return (
    <div>
      <TableContainer sx={containerStyles}>
        <Table size='small' stickyHeader {...getTableProps()}>
          <TableHead>{headers}</TableHead>
          <TableBody {...getTableBodyProps()}>{rows}</TableBody>
        </Table>
      </TableContainer>
      <ContractReviewTablePagination instance={instance} totalCount={totalCount} />
    </div>
  );
}

interface ContractReviewTableRowProps extends TableRowProps {
  row: Row<ContractReviewGridItem>;
}

function ContractReviewTableRow({ row, ...props }: ContractReviewTableRowProps) {
  const state = useContractReviewListState();

  const highlight = state.contractId.get() === row.id;
  return (
    <TableRow selected={highlight} {...props}>
      {row.cells.map(cell => (
        <TableCell align={cell.column.align} {...cell.getCellProps()}>
          {cell.render('Cell')}
        </TableCell>
      ))}
    </TableRow>
  );
}
